import { c as _c } from "react/compiler-runtime";
import { InterfaceChainUtils, type InterfaceToken } from '@morpho-org/dapp-core';
import { formatLongString, isDefined } from '@morpho-org/morpho-ts';
import { NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { formatters, getDigitsFromPriceAndAmount, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { Badge, Button, Separator } from '@repo/ui';
import { useMemo } from 'react';
import { MobileCardRow } from '@/components/common/MobileCardRow';
import type { BaseMobileBorrowCardProps } from './MobileBorrowCard.types';
import MobileBorrowHeader from './components/MobileBorrowHeader';
import MobileMarketHealth from './components/MobileMarketHealth/MobileMarketHealth';
import { useBorrowCard } from './hooks/useBorrowCard';
const percentageFormatter = formatters.percent.default('-').digits(2).unit('%');
const srcAvatarProps = {
  size: 'small'
} as const;
interface MobileMarketPositionCardProps extends Omit<BaseMobileBorrowCardProps, 'lltv'> {
  collateral: {
    token: InterfaceToken | null;
    amount?: bigint | null;
    usdValue?: bigint | null;
  };
  loan: {
    token: InterfaceToken;
    amount?: bigint | null;
    usdValue?: bigint | null;
  };
  lltv: bigint;
  ltv?: bigint;
  displayNetwork?: boolean;
  hideActionButtons?: boolean;
  utilization?: number;
}
export default function MobileMarketPositionCard(t0) {
  const $ = _c(84);
  const {
    href,
    collateral,
    loan,
    lltv,
    ltv,
    marketId,
    netApy,
    apy,
    rewards,
    points,
    warnings,
    displayNetwork,
    hideActionButtons,
    utilization
  } = t0;
  let t1;
  if ($[0] !== lltv || $[1] !== loan || $[2] !== marketId) {
    t1 = {
      marketId,
      loan,
      lltv
    };
    $[0] = lltv;
    $[1] = loan;
    $[2] = marketId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    formattedLltv,
    onBorrowClick,
    onRepayClick
  } = useBorrowCard(t1);
  collateral.token?.symbol;
  const t2 = collateral.token?.symbol || UNKNOWN_SYMBOL;
  let t3;
  if ($[4] !== t2) {
    t3 = formatLongString(t2, 16);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const truncatedCollateralSymbol = t3;
  let t4;
  if ($[6] !== collateral.amount || $[7] !== collateral.token?.decimals || $[8] !== collateral.token?.price || $[9] !== collateral.usdValue || $[10] !== truncatedCollateralSymbol) {
    t4 = () => ({
      formattedCollateralAmount: formatters.short.digits(getDigitsFromPriceAndAmount(collateral.token?.price, collateral.amount, collateral.token?.decimals)).unit(truncatedCollateralSymbol).of(collateral.amount, collateral.token?.decimals),
      formattedCollateralUsd: formatters.short.unit("$").digits(2).of(collateral.usdValue, 18)
    });
    $[6] = collateral.amount;
    $[7] = collateral.token?.decimals;
    $[8] = collateral.token?.price;
    $[9] = collateral.usdValue;
    $[10] = truncatedCollateralSymbol;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  collateral.token?.decimals;
  collateral.token?.price;
  let t5;
  if ($[12] !== t4) {
    t5 = t4();
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    formattedCollateralAmount,
    formattedCollateralUsd
  } = t5;
  let t6;
  if ($[14] !== loan.amount || $[15] !== loan.token.decimals || $[16] !== loan.token.price || $[17] !== loan.token.symbol) {
    t6 = formatters.short.digits(getDigitsFromPriceAndAmount(loan.token.price, loan.amount, loan.token.decimals)).readable().unit(loan.token.symbol).of(loan.amount, loan.token.decimals);
    $[14] = loan.amount;
    $[15] = loan.token.decimals;
    $[16] = loan.token.price;
    $[17] = loan.token.symbol;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== loan.usdValue) {
    t7 = formatters.short.unit("$").digits(2).of(loan.usdValue, 18);
    $[19] = loan.usdValue;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = {
      formattedLoanAmount: t6,
      formattedLoanUsd: t7
    };
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    formattedLoanAmount,
    formattedLoanUsd
  } = t8;
  let t9;
  if ($[24] !== loan.token.chain.id) {
    t9 = InterfaceChainUtils.getMetadata(loan.token.chain.id);
    $[24] = loan.token.chain.id;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const networkName = t9.chain.name;
  const t10 = collateral.token?.logoURI;
  const t11 = collateral.token ? collateral.token.symbol ?? UNKNOWN_SYMBOL : undefined;
  let t12;
  if ($[26] !== apy || $[27] !== collateral.token || $[28] !== formattedLltv || $[29] !== href || $[30] !== loan.token.logoURI || $[31] !== loan.token.symbol || $[32] !== netApy || $[33] !== points || $[34] !== rewards || $[35] !== t10 || $[36] !== t11 || $[37] !== warnings) {
    t12 = <MobileBorrowHeader prefetch={true} href={href} loanLogoURI={loan.token.logoURI} loanSymbol={loan.token.symbol} collateralLogoURI={t10} collateralSymbol={t11} lltv={formattedLltv} netApy={netApy} apy={apy} rewards={rewards} points={points} rewardAsset={collateral.token} warnings={warnings} />;
    $[26] = apy;
    $[27] = collateral.token;
    $[28] = formattedLltv;
    $[29] = href;
    $[30] = loan.token.logoURI;
    $[31] = loan.token.symbol;
    $[32] = netApy;
    $[33] = points;
    $[34] = rewards;
    $[35] = t10;
    $[36] = t11;
    $[37] = warnings;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Separator className="my-xs" />;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== displayNetwork || $[41] !== loan.token.chain.id || $[42] !== networkName) {
    t14 = displayNetwork && <MobileCardRow label="Network"><div className="gap-2xs flex items-center"><Network chainId={loan.token.chain.id} srcAvatarProps={srcAvatarProps} /><span className="text-3xs capitalize">{networkName}</span></div></MobileCardRow>;
    $[40] = displayNetwork;
    $[41] = loan.token.chain.id;
    $[42] = networkName;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      lineHeight: "normal"
    };
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== formattedLoanAmount) {
    t16 = <span className="text-3xs text-foreground" style={t15}>{formattedLoanAmount}</span>;
    $[45] = formattedLoanAmount;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== formattedLoanAmount || $[48] !== formattedLoanUsd) {
    t17 = formattedLoanAmount && formattedLoanUsd && <Badge variant="tertiary" size="sm" className="my-auto">{formattedLoanUsd}</Badge>;
    $[47] = formattedLoanAmount;
    $[48] = formattedLoanUsd;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== t16 || $[51] !== t17) {
    t18 = <MobileCardRow label="Loan"><div className="gap-2xs flex items-center">{t16}{t17}</div></MobileCardRow>;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      lineHeight: "normal"
    };
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== formattedCollateralAmount) {
    t20 = <span className="text-3xs text-foreground" style={t19}>{formattedCollateralAmount}</span>;
    $[54] = formattedCollateralAmount;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== formattedCollateralAmount || $[57] !== formattedCollateralUsd) {
    t21 = formattedCollateralAmount && formattedCollateralUsd && <Badge variant="tertiary" size="sm" className="my-auto">{formattedCollateralUsd}</Badge>;
    $[56] = formattedCollateralAmount;
    $[57] = formattedCollateralUsd;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== t20 || $[60] !== t21) {
    t22 = <MobileCardRow label="Collateral"><div className="gap-2xs flex items-center">{t20}{t21}</div></MobileCardRow>;
    $[59] = t20;
    $[60] = t21;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      lineHeight: "normal"
    };
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== utilization) {
    t24 = percentageFormatter.of(utilization);
    $[63] = utilization;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t24) {
    t25 = <MobileCardRow label="Utilization"><span className="text-3xs text-foreground" style={t23}>{t24}</span></MobileCardRow>;
    $[65] = t24;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== lltv || $[68] !== loan.token.chain.id || $[69] !== ltv || $[70] !== marketId) {
    t26 = isDefined(ltv) && <><Separator className="my-xs" /><MobileMarketHealth ltv={ltv} lltv={lltv} marketId={marketId} chainId={loan.token.chain.id} /></>;
    $[67] = lltv;
    $[68] = loan.token.chain.id;
    $[69] = ltv;
    $[70] = marketId;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== hideActionButtons || $[73] !== onBorrowClick || $[74] !== onRepayClick) {
    t27 = !hideActionButtons && <><Separator className="my-xs" /><div className="gap-sm flex"><Button className="flex-1 basis-[calc(100%/3)]" onClick={onBorrowClick}>Borrow</Button><Button className="flex-1 basis-[calc(100%/3)]" variant="secondary" onClick={onRepayClick}>Repay</Button></div></>;
    $[72] = hideActionButtons;
    $[73] = onBorrowClick;
    $[74] = onRepayClick;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  let t28;
  if ($[76] !== t12 || $[77] !== t14 || $[78] !== t18 || $[79] !== t22 || $[80] !== t25 || $[81] !== t26 || $[82] !== t27) {
    t28 = <article className="text-foreground p-md gap-sm bg-card flex flex-col rounded-[20px] shadow-md dark:shadow-none">{t12}{t13}{t14}{t18}{t22}{t25}{t26}{t27}</article>;
    $[76] = t12;
    $[77] = t14;
    $[78] = t18;
    $[79] = t22;
    $[80] = t25;
    $[81] = t26;
    $[82] = t27;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  return t28;
}